import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { api } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { LoginCredentials, LoginResponse } from '@/types'
import { toast } from 'sonner'

const AUTH_QUERY_KEY = 'auth'

export const useLogin = () => {
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<LoginResponse> => {
      // Note: This uses the regular auth endpoint, not /admin
      // The backend will include role in the JWT if user is admin
      const { data } = await api.post('/auth/login', credentials)
      return data
    },
    onSuccess: (data) => {
      // Verify user has admin role
      const allowedRoles = ['super_admin', 'admin', 'finance', 'support']
      if (!allowedRoles.includes(data.user.role)) {
        toast.error('Acceso denegado. Solo administradores pueden ingresar.')
        return
      }

      setAuth(data.user, data.access_token, data.refresh_token)
      queryClient.setQueryData([AUTH_QUERY_KEY, 'me'], data.user)
      toast.success(`Bienvenido, ${data.user.name}`)
      navigate({ to: '/' })
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { detail?: string } } }
      const message = axiosError.response?.data?.detail || 'Error al iniciar sesión'
      toast.error(message)
    },
  })
}

export const useLogout = () => {
  const navigate = useNavigate()
  const logout = useAuthStore((state) => state.logout)
  const queryClient = useQueryClient()

  return () => {
    logout()
    queryClient.clear()
    navigate({ to: '/login' })
    toast.info('Sesión cerrada')
  }
}

export const useCurrentUser = () => {
  const token = useAuthStore((state) => state.token)
  const setAuth = useAuthStore((state) => state.setAuth)
  const setLoading = useAuthStore((state) => state.setLoading)

  return useQuery({
    queryKey: [AUTH_QUERY_KEY, 'me'],
    queryFn: async () => {
      if (!token) {
        setLoading(false)
        throw new Error('No token')
      }
      const { data } = await api.get('/auth/me')
      // Keep zustand store in sync with fresh user data
      if (data && token) {
        setAuth(data, token)
      }
      return data
    },
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export const useHasRole = (roles: string[]) => {
  const user = useAuthStore((state) => state.user)
  if (!user) return false
  return roles.includes(user.role)
}

export const useHasPermission = (permission: string) => {
  const user = useAuthStore((state) => state.user)
  if (!user) return false
  if (user.role === 'super_admin') return true
  return user.permissions.includes(permission) || user.permissions.includes('*')
}
